import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  User,
  Mail,
  Phone,
  Calendar,
  CheckCircle,
  XCircle,
  AlertCircle,
  Trash2,
  MapPin,
  Globe,
  Clock,
  CreditCard,
  Star,
  ShoppingBag,
  MessageSquare,
  Shield,
  DollarSign,
  Heart,
  Activity,
  Eye,
  EyeOff,
  ListChecks,
  Loader2,
} from 'lucide-react';
import { Button } from '../../../components/shared';
import { useToastContext } from "../../../contexts/ToastContext";
import { 
  useClient, 
  useUpdateClientStatus, 
  useDeleteClient
} from '../../../services/clientService';
import { AdminPageHeader, AdminGuideCallout } from '../../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../../utils/adminPageGuides';

const AdminClientDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { success, error: showError } = useToastContext();

  // Fetch client data
  const { 
    data: clientData, 
    isLoading: loading, 
    error: clientError 
  } = useClient(id);

  const client = clientData?.data;

  // API mutations
  const updateStatusMutation = useUpdateClientStatus({
    onSuccess: () => {
      success('Client status updated successfully', 'success');
    },
    onError: (error) => {
      showError(error.response?.data?.message || 'Failed to update client status', 'error');
    }
  });

  const deleteClientMutation = useDeleteClient({
    onSuccess: () => {
      success('Client deleted successfully', 'success');
      navigate('/admin/clients');
    },
    onError: (error) => {
      showError(error.response?.data?.message || 'Failed to delete client', 'error');
    }
  });

  const handleToggleStatus = () => {
    if (client) {
      updateStatusMutation.mutate({
        id: client.id,
        status: !client.is_active
      });
    }
  };

  const handleDeleteClient = () => {
    if (client && window.confirm(`Are you sure you want to delete ${client.name || client.email}?`)) {
      deleteClientMutation.mutate(client.id);
    }
  };

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="py-20 text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading client details…</p>
        </div>
      </div>
    );
  }

  if (clientError) {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-red-200 dark:border-red-800/50 bg-red-50/80 dark:bg-red-950/30 p-8 text-center">
          <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-red-800 dark:text-red-200 mb-2">Error Loading Client</h3>
          <p className="text-red-600 dark:text-red-300 text-sm mb-6">
            {clientError.message || 'Failed to load client details'}
          </p>
          <Button className="!rounded-xl" onClick={() => navigate('/admin/clients')}>
            Back to Clients
          </Button>
        </div>
      </div>
    );
  }

  if (!client) {
    return (
      <div className="space-y-5">
        <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-8 text-center">
          <User className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-50 mb-2">Client Not Found</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
            The client you're looking for doesn't exist.
          </p>
          <Button className="!rounded-xl" onClick={() => navigate('/admin/clients')}>
            Back to Clients
          </Button>
        </div>
      </div>
    );
  }

  // Calculate statistics
  const summary = client?.client_summary || {};
  const bookings = [...(client?.bookings || [])].sort((a, b) => {
    const aDate = a.event_date || a.created_at || '';
    const bDate = b.event_date || b.created_at || '';
    return String(bDate).localeCompare(String(aDate));
  });
  const reviews = client?.reviews || [];
  const favorites = client?.favorite_vendors || client?.favoriteVendors || [];
  const bookingsCount = summary.bookings_count ?? bookings.length;
  const reviewsCount = summary.reviews_count ?? reviews.length;
  const favoritesCount = summary.favorites_count ?? favorites.length;
  const upcomingCount = summary.upcoming_bookings ?? 0;
  const totalSpent = Number(summary.total_spent || 0);
  const statusCounts = summary.status_counts || {};
  const averageRating = reviews.length > 0
    ? (reviews.reduce((sum, review) => sum + (review.rating || 0), 0) / reviews.length).toFixed(1)
    : null;

  const formatMoney = (amount, currency = 'USD') => {
    const value = Number(amount || 0);
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency || 'USD',
        maximumFractionDigits: 2,
      }).format(value);
    } catch {
      return `$${value.toFixed(2)}`;
    }
  };

  const bookingStatusClass = (status) => {
    const map = {
      confirmed: 'bg-blue-100 text-blue-800 dark:bg-blue-950/40 dark:text-blue-300',
      completed: 'bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-300',
      pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/40 dark:text-yellow-300',
      cancelled: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
      refunded: 'bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-300',
      disputed: 'bg-orange-100 text-orange-800 dark:bg-orange-950/40 dark:text-orange-300',
      in_progress: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/40 dark:text-indigo-300',
    };
    return map[status] || 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
  };

  // Format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title={client.name || 'Client'}
        subtitle="Review account details, bookings, and status for this client."
        actions={
          <Link
            to="/admin/clients"
            className="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-1.5" />
            Back to Clients
          </Link>
        }
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.clientDetail.title}
        steps={ADMIN_PAGE_GUIDES.clientDetail.steps}
        icon={ListChecks}
      />

      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-start sm:items-center gap-4">
                <div className="flex-shrink-0">
                  {client.profile_image_url || client.avatar ? (
                    <img
                      className="h-16 w-16 sm:h-20 sm:w-20 rounded-full object-cover border-2 border-gray-200 dark:border-gray-700"
                      src={client.profile_image_url || client.avatar}
                      alt={client.name}
                    />
                  ) : (
                    <div className="h-16 w-16 sm:h-20 sm:w-20 rounded-full bg-gray-900 dark:bg-gray-100 flex items-center justify-center border-2 border-gray-200 dark:border-gray-700">
                      <User className="h-8 w-8 sm:h-10 sm:w-10 text-white dark:text-gray-900" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-50 truncate">
                    {client.name || 'No Name'}
                  </h2>
                  <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-1 truncate">{client.email}</p>
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                      client.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {client.is_active ? (
                        <CheckCircle className="h-3 w-3 mr-1.5" />
                      ) : (
                        <XCircle className="h-3 w-3 mr-1.5" />
                      )}
                      {client.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {client.is_verified && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <Shield className="h-3 w-3 mr-1.5" />
                        Verified
                      </span>
                    )}
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                      {client.user_type || 'client'}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                <Button
                  onClick={handleToggleStatus}
                  variant="outline"
                  size="sm"
                  className={`!rounded-xl flex items-center justify-center ${
                    client.is_active 
                      ? 'border-red-300 text-red-700 hover:bg-red-50' 
                      : 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                  }`}
                  disabled={updateStatusMutation.isLoading}
                >
                  {client.is_active ? (
                    <>
                      <EyeOff className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Deactivate</span>
                      <span className="sm:hidden">Deactivate</span>
                    </>
                  ) : (
                    <>
                      <Eye className="w-4 h-4 mr-2" />
                      <span className="hidden sm:inline">Activate</span>
                      <span className="sm:hidden">Activate</span>
                    </>
                  )}
                </Button>
                
                <Button
                  onClick={handleDeleteClient}
                  variant="outline"
                  size="sm"
                  className="!rounded-xl flex items-center justify-center border-red-300 text-red-700 hover:bg-red-50"
                  disabled={deleteClientMutation.isLoading}
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  <span className="hidden sm:inline">Delete</span>
                  <span className="sm:hidden">Delete</span>
                </Button>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3 sm:gap-4">
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">Total spent</p>
                  <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-50">{formatMoney(totalSpent)}</p>
                </div>
                <div className="p-2 bg-emerald-100 dark:bg-emerald-950/40 rounded-lg">
                  <DollarSign className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">Bookings</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-50">{bookingsCount}</p>
                  {upcomingCount > 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{upcomingCount} upcoming</p>
                  )}
                </div>
                <div className="p-2 bg-blue-100 dark:bg-blue-950/40 rounded-lg">
                  <ShoppingBag className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">Reviews</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-50">{reviewsCount}</p>
                  {averageRating && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{averageRating}/5 avg</p>
                  )}
                </div>
                <div className="p-2 bg-teal-100 dark:bg-teal-950/40 rounded-lg">
                  <MessageSquare className="h-5 w-5 sm:h-6 sm:w-6 text-teal-600 dark:text-teal-400" />
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">Saved vendors</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-50">{favoritesCount}</p>
                </div>
                <div className="p-2 bg-rose-100 dark:bg-rose-950/40 rounded-lg">
                  <Heart className="h-5 w-5 sm:h-6 sm:w-6 text-rose-600 dark:text-rose-400" />
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 col-span-2 sm:col-span-1">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mb-1">Member since</p>
                  <p className="text-xs sm:text-sm font-semibold text-gray-900 dark:text-gray-50">{formatDate(client.created_at)}</p>
                </div>
                <div className="p-2 bg-green-100 dark:bg-green-950/40 rounded-lg">
                  <Calendar className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </div>
          </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left Column - Main Details */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-6">
            {/* Basic Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
                <User className="h-5 w-5 mr-2 text-gray-400" />
                Basic Information
              </h2>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <dt className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Full Name</dt>
                  <dd className="text-sm sm:text-base text-gray-900 font-medium">{client.name || 'Not provided'}</dd>
                </div>
                <div>
                  <dt className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Email Address</dt>
                  <dd className="text-sm sm:text-base text-gray-900 flex items-center">
                    <Mail className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                    <span className="truncate">{client.email}</span>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Phone Number</dt>
                  <dd className="text-sm sm:text-base text-gray-900 flex items-center">
                    <Phone className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                    {client.phone || 'Not provided'}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs sm:text-sm font-medium text-gray-500 mb-1">User Type</dt>
                  <dd className="mt-1">
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {client.user_type || 'client'}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Account Status</dt>
                  <dd className="mt-1">
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                      client.is_active 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {client.is_active ? (
                        <>
                          <CheckCircle className="h-3 w-3 mr-1.5" />
                          Active
                        </>
                      ) : (
                        <>
                          <XCircle className="h-3 w-3 mr-1.5" />
                          Inactive
                        </>
                      )}
                    </span>
                  </dd>
                </div>
                <div>
                  <dt className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Email Verified</dt>
                  <dd className="mt-1">
                    {client.email_verified_at ? (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircle className="h-3 w-3 mr-1.5" />
                        Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                        <XCircle className="h-3 w-3 mr-1.5" />
                        Not Verified
                      </span>
                    )}
                  </dd>
                </div>
              </dl>
            </div>

            {/* Location Information */}
            {(client.country || client.city || client.state || client.latitude) && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-gray-400" />
                  Location Information
                </h2>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  {client.country && (
                    <div>
                      <dt className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Country</dt>
                      <dd className="text-sm sm:text-base text-gray-900 flex items-center">
                        <Globe className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                        {client.country} {client.country_code && `(${client.country_code})`}
                      </dd>
                    </div>
                  )}
                  {client.city && (
                    <div>
                      <dt className="text-xs sm:text-sm font-medium text-gray-500 mb-1">City</dt>
                      <dd className="text-sm sm:text-base text-gray-900">{client.city}</dd>
                    </div>
                  )}
                  {client.state && (
                    <div>
                      <dt className="text-xs sm:text-sm font-medium text-gray-500 mb-1">State/Region</dt>
                      <dd className="text-sm sm:text-base text-gray-900">{client.state}</dd>
                    </div>
                  )}
                  {(client.latitude && client.longitude) && (
                    <div>
                      <dt className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Coordinates</dt>
                      <dd className="text-xs sm:text-sm text-gray-600 font-mono">
                        {parseFloat(client.latitude).toFixed(6)}, {parseFloat(client.longitude).toFixed(6)}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            {/* Subscription Information */}
            {(client.subscription_id || client.is_subscription_active !== undefined) && (
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
                  <CreditCard className="h-5 w-5 mr-2 text-gray-400" />
                  Subscription Information
                </h2>
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                  <div>
                    <dt className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Subscription Status</dt>
                    <dd className="mt-1">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                        client.is_subscription_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        {client.is_subscription_active ? 'Active' : 'Inactive'}
                      </span>
                    </dd>
                  </div>
                  {client.subscription_expires_at && (
                    <div>
                      <dt className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Expires At</dt>
                      <dd className="text-sm sm:text-base text-gray-900">{formatDate(client.subscription_expires_at)}</dd>
                    </div>
                  )}
                </dl>
              </div>
            )}

            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
              <div className="flex items-center justify-between gap-3 mb-4">
                <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-50 flex items-center">
                  <ShoppingBag className="h-5 w-5 mr-2 text-gray-400" />
                  Bookings
                </h2>
                <span className="text-xs text-gray-500 dark:text-gray-400">{bookingsCount} total</span>
              </div>
              {Object.keys(statusCounts).length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {Object.entries(statusCounts).map(([status, count]) => (
                    <span
                      key={status}
                      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize ${bookingStatusClass(status)}`}
                    >
                      {String(status).replaceAll('_', ' ')} · {count}
                    </span>
                  ))}
                </div>
              )}
              {bookings.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No bookings yet.</p>
              ) : (
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <table className="min-w-full text-sm">
                    <thead className="text-left text-xs text-gray-500 dark:text-gray-400">
                      <tr>
                        <th className="px-4 sm:px-0 py-2 font-medium">Event</th>
                        <th className="px-3 py-2 font-medium">Vendor</th>
                        <th className="px-3 py-2 font-medium">Status</th>
                        <th className="px-3 py-2 font-medium text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map((booking) => {
                        const total = (Number(booking.subtotal_amount) || 0)
                          + (Number(booking.platform_fee) || 0)
                          + (Number(booking.tax_amount) || 0);
                        return (
                          <tr key={booking.id} className="border-t border-gray-100 dark:border-gray-800">
                            <td className="px-4 sm:px-0 py-3 align-top">
                              <Link
                                to={`/admin/bookings/${booking.id}`}
                                className="font-medium text-gray-900 dark:text-gray-100 hover:underline"
                              >
                                {booking.booking_reference || 'Booking'}
                              </Link>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {formatDate(booking.event_date)}
                                {booking.start_time ? ` · ${String(booking.start_time).slice(0, 5)}` : ''}
                              </p>
                              {booking.vendor_service?.service_name && (
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {booking.vendor_service.service_name}
                                </p>
                              )}
                            </td>
                            <td className="px-3 py-3 align-top text-gray-700 dark:text-gray-300">
                              {booking.vendor?.id ? (
                                <Link to={`/admin/vendors/${booking.vendor.id}`} className="hover:underline">
                                  {booking.vendor.business_name || 'Vendor'}
                                </Link>
                              ) : (
                                '—'
                              )}
                            </td>
                            <td className="px-3 py-3 align-top">
                              <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium capitalize ${bookingStatusClass(booking.status)}`}>
                                {String(booking.status || 'unknown').replaceAll('_', ' ')}
                              </span>
                            </td>
                            <td className="px-3 py-3 align-top text-right text-gray-900 dark:text-gray-100 whitespace-nowrap">
                              {formatMoney(total, booking.currency)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4 flex items-center">
                <Star className="h-5 w-5 mr-2 text-gray-400" />
                Reviews
              </h2>
              {reviews.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No reviews yet.</p>
              ) : (
                <div className="space-y-3">
                  {reviews.map((review) => (
                    <div key={review.id} className="rounded-xl border border-gray-100 dark:border-gray-800 p-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                          {review.vendor?.business_name || 'Vendor'}
                        </p>
                        <span className="inline-flex items-center text-xs text-amber-600 dark:text-amber-400">
                          <Star className="h-3.5 w-3.5 mr-1 fill-current" />
                          {review.rating || 0}/5
                        </span>
                      </div>
                      {review.title && (
                        <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">{review.title}</p>
                      )}
                      {review.comment && (
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 line-clamp-3">{review.comment}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-2">{formatDate(review.created_at)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Account & Activity */}
          <div className="space-y-4 sm:space-y-6">
            {/* Account Information */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
                <Calendar className="h-5 w-5 mr-2 text-gray-400" />
                Account Information
              </h2>
              <dl className="space-y-4">
                <div>
                  <dt className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Member Since</dt>
                  <dd className="text-sm sm:text-base text-gray-900 flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                    {formatDate(client.created_at)}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Last Updated</dt>
                  <dd className="text-sm sm:text-base text-gray-900 flex items-center">
                    <Calendar className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                    {formatDate(client.updated_at)}
                  </dd>
                </div>
                {client.last_logged_in && (
                  <div>
                    <dt className="text-xs sm:text-sm font-medium text-gray-500 mb-1">Last Login</dt>
                    <dd className="text-sm sm:text-base text-gray-900 flex items-center">
                      <Clock className="h-4 w-4 mr-2 text-gray-400 flex-shrink-0" />
                      {formatDateTime(client.last_logged_in)}
                    </dd>
                  </div>
                )}
                {client.ip_address && (
                  <div>
                    <dt className="text-xs sm:text-sm font-medium text-gray-500 mb-1">IP Address</dt>
                    <dd className="text-xs sm:text-sm text-gray-600 font-mono">{client.ip_address}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Saved vendors */}
            <div className="bg-white dark:bg-gray-900 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-50 mb-4 flex items-center">
                <Heart className="h-5 w-5 mr-2 text-gray-400" />
                Saved vendors
              </h2>
              {favorites.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400">No saved vendors.</p>
              ) : (
                <div className="space-y-2">
                  {favorites.map((favorite) => {
                    const vendor = favorite.vendor;
                    if (!vendor) return null;
                    return (
                      <Link
                        key={favorite.id || vendor.id}
                        to={`/admin/vendors/${vendor.id}`}
                        className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 dark:bg-gray-800/60 px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-800"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {vendor.business_name || 'Vendor'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {[vendor.city, vendor.country].filter(Boolean).join(', ') || '—'}
                          </p>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Activity Summary */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 sm:p-6">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 sm:mb-6 flex items-center">
                <Activity className="h-5 w-5 mr-2 text-gray-400" />
                Activity Summary
              </h2>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 rounded-lg">
                  <div className="flex items-center">
                    <ShoppingBag className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-300">Total bookings</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{bookingsCount}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 rounded-lg">
                  <div className="flex items-center">
                    <DollarSign className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-300">Completed spend</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{formatMoney(totalSpent)}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800/60 rounded-lg">
                  <div className="flex items-center">
                    <Heart className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600 dark:text-gray-300">Saved vendors</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">{favoritesCount}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <MessageSquare className="h-4 w-4 text-gray-400 mr-2" />
                    <span className="text-sm text-gray-600">Reviews Given</span>
                  </div>
                  <span className="text-sm font-semibold text-gray-900">{reviewsCount}</span>
                </div>
                {averageRating && (
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <Star className="h-4 w-4 text-gray-400 mr-2 fill-current" />
                      <span className="text-sm text-gray-600">Average Rating</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{averageRating}/5.0</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
    </div>
  );
};

export default AdminClientDetail;
